// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import { FORMID, TestRunParams } from "../TestRunConstants";
import { ROUTE_DEFINITIONS, getPath } from "routes/routes";
import React, { memo, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";

import { Question } from "components/ui/Questionnaire/QuestionnaireTypes";
import QuestionnaireForm from "../../ui/Questionnaire/Questionnaire";
import QuestionnaireInput from "components/ui/Questionnaire/QuestionnaireInput";
import { getFirebaseAppSync } from "components/Firebase/Firebase";
import { setStepReady } from "components/TestRun/TestRunStepReadyEmitter";
import { useAuthState } from "../../../store/auth";
import { useTestRunConfig } from "components/TestRun/useTestRunConfig";
import { useTestRunState } from "store/testrun";
import { validateEmail } from "../../ui/Questionnaire/validators/QuestionnaireValidators";

const EmailPanel = memo(() => {
  const { getNextDefaultStep } = useTestRunConfig();
  const { step, testRunUID } = useParams<TestRunParams>();
  const QuestionnaireFormMemoized = memo(QuestionnaireForm);
  const history = useHistory();
  const firebaseApp = getFirebaseAppSync();
  const { auth } = useAuthState();
  const testRunState = useTestRunState();

  const stepReadyCallback = useCallback(
    (stepReady: boolean) => {
      setStepReady(step, stepReady);
    },
    [step]
  );

  const barcode = testRunState.testRun?.registration?.barcode || "";

  const onSubmit = useCallback(
    async (event: React.FormEvent, questions: { [key: string]: Question }) => {
      event.preventDefault();

      const newEmail = questions["newEmail"]
        ? questions["newEmail"].value
        : null;

      if (newEmail) {
        await firebaseApp.updateEmail(newEmail, barcode, "TEST"); // TODO: USC-1206 - integrate orgID better
      }

      // Resets the state.
      stepReadyCallback(false);
      const nextStep = getNextDefaultStep({
        currentStepName: step!,
      });
      const path = getPath(ROUTE_DEFINITIONS.PERFORMTEST, {
        params: {
          ":testRunUID": testRunUID,
          ":step": nextStep!,
        },
      });
      history.push(path);
    },
    [
      barcode,
      firebaseApp,
      getNextDefaultStep,
      history,
      step,
      stepReadyCallback,
      testRunUID,
    ]
  );

  // TODO: Generic Error handler.
  if (!auth) {
    return <div>Oops, something went wrong</div>;
  }

  if (!testRunState.subscribed) {
    return <div />; // Add Loader
  }

  return (
    <QuestionnaireFormMemoized
      formId={FORMID}
      onSubmit={onSubmit}
      onFormReady={stepReadyCallback}
    >
      <QuestionnaireInput
        label="Enter email"
        mandatory={true}
        name="newEmail"
        type="text"
        validators={[validateEmail]}
      />
    </QuestionnaireFormMemoized>
  );
});

export default EmailPanel;
